import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type RefObject,
} from 'react';
import {
  buildPedalSegments,
  buildRollNoteRects,
  computePianoRollLayout,
  divisionsToX,
  isBlackKey,
  midiToY,
  pitchLabel,
  velocityBarHeight,
  velocityFromLaneY,
  xToDivisions,
  yToMidi,
  type RollEvent,
  type RollNoteRect,
  type RollPedalEvent,
} from '../composition/pianoRoll';
import './CompositionWorkspace.css';

const DIVISIONS_PER_BEAT = 4;

type PitchDrag = { rect: RollNoteRect; pointerId: number; midi: number };

/**
 * The score seen as time and pitch. It shares the score's selection, cursor and
 * playhead, so a note chosen here is the same note chosen there; what it adds is a
 * direct view of duration, touch and pedal that engraving has to abstract away.
 */
function PianoRollComponent({
  events,
  pedalEvents,
  totalDivisions,
  measureDurationDivisions,
  zoom,
  selectedEventIds,
  selectedNoteTarget,
  reviewFlaggedEventIds,
  recordingCursorDivisions,
  playbackClock,
  playbackTempo,
  isPlayingBack,
  editable,
  onSelect,
  onVelocityChange,
  onPitchDrag,
  onCursorChange,
}: {
  events: RollEvent[];
  pedalEvents: RollPedalEvent[];
  totalDivisions: number;
  measureDurationDivisions: number;
  zoom: number;
  selectedEventIds: string[];
  selectedNoteTarget: { eventId: string; midi: number } | null;
  reviewFlaggedEventIds?: Set<string>;
  recordingCursorDivisions: number;
  playbackClock: RefObject<{ startedAtMs: number; startDivisions: number } | null>;
  playbackTempo: number;
  isPlayingBack: boolean;
  editable: boolean;
  onSelect: (eventId: string, midi: number, options?: { toggleSelection?: boolean }) => void;
  onVelocityChange: (eventId: string, midi: number, velocity: number) => void;
  onPitchDrag: (eventId: string, midi: number, nextMidi: number) => void;
  onCursorChange: (divisions: number) => void;
}) {
  const layout = useMemo(
    () => computePianoRollLayout(events, totalDivisions, { zoom }),
    [events, totalDivisions, zoom],
  );
  const rects = useMemo(() => buildRollNoteRects(events, layout), [events, layout]);
  const pedalSegments = useMemo(
    () => buildPedalSegments(pedalEvents, totalDivisions, layout),
    [pedalEvents, totalDivisions, layout],
  );
  const selected = useMemo(() => new Set(selectedEventIds), [selectedEventIds]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<SVGLineElement>(null);
  const [pitchDrag, setPitchDrag] = useState<PitchDrag | null>(null);

  const gridTop = 0;
  const velocityTop = layout.gridHeight + 8;
  const pedalTop = velocityTop + layout.velocityLaneHeight + 6;
  const totalHeight = pedalTop + layout.pedalLaneHeight + 4;
  const totalWidth = layout.keyboardWidth + layout.gridWidth;

  // The playhead follows the transport's own clock rather than React state, so it
  // moves every frame and never lags a render behind the sound.
  useEffect(() => {
    if (!isPlayingBack) {
      playheadRef.current?.setAttribute('opacity', '0');
      return undefined;
    }
    let frame = 0;
    const tick = () => {
      const clock = playbackClock.current;
      const line = playheadRef.current;
      if (clock && line) {
        const elapsedMs = performance.now() - clock.startedAtMs;
        const divisionMs = 60000 / Math.max(1, playbackTempo) / DIVISIONS_PER_BEAT;
        const divisions = clock.startDivisions + elapsedMs / divisionMs;
        const x = divisionsToX(layout, divisions);
        line.setAttribute('x1', String(x));
        line.setAttribute('x2', String(x));
        line.setAttribute('opacity', '1');
        const scroller = scrollRef.current;
        if (scroller) {
          const visibleLeft = scroller.scrollLeft;
          const visibleRight = visibleLeft + scroller.clientWidth;
          if (x < visibleLeft + layout.keyboardWidth || x > visibleRight - 24)
            scroller.scrollLeft = Math.max(0, x - scroller.clientWidth / 3);
        }
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [isPlayingBack, playbackClock, playbackTempo, layout]);

  const localPoint = (event: PointerEvent<SVGElement>) => {
    const svg = event.currentTarget.ownerSVGElement ?? (event.currentTarget as SVGSVGElement);
    const bounds = svg.getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  };

  const handleGridPointerDown = (event: PointerEvent<SVGRectElement>) => {
    const point = localPoint(event);
    onCursorChange(Math.max(0, Math.round(xToDivisions(layout, point.x))));
  };

  const handleNotePointerDown = (rect: RollNoteRect) => (event: PointerEvent<SVGRectElement>) => {
    event.stopPropagation();
    onSelect(rect.eventId, rect.midi, { toggleSelection: event.shiftKey || event.metaKey });
    if (!editable) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setPitchDrag({ rect, pointerId: event.pointerId, midi: rect.midi });
  };

  const handleNotePointerMove = (event: PointerEvent<SVGRectElement>) => {
    if (!pitchDrag || event.pointerId !== pitchDrag.pointerId) return;
    const point = localPoint(event);
    const midi = yToMidi(layout, point.y - gridTop);
    if (midi !== pitchDrag.midi) setPitchDrag({ ...pitchDrag, midi });
  };

  const handleNotePointerUp = (event: PointerEvent<SVGRectElement>) => {
    if (!pitchDrag || event.pointerId !== pitchDrag.pointerId) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    if (pitchDrag.midi !== pitchDrag.rect.midi)
      onPitchDrag(pitchDrag.rect.eventId, pitchDrag.rect.midi, pitchDrag.midi);
    setPitchDrag(null);
  };

  const applyVelocityAt = (rect: RollNoteRect, event: PointerEvent<SVGRectElement>) => {
    const point = localPoint(event);
    onVelocityChange(
      rect.eventId,
      rect.midi,
      velocityFromLaneY(point.y - velocityTop, layout.velocityLaneHeight),
    );
  };

  const barCount = Math.ceil(totalDivisions / Math.max(1, measureDurationDivisions));
  const rows = Array.from(
    { length: layout.topMidi - layout.bottomMidi + 1 },
    (_, index) => layout.topMidi - index,
  );

  return (
    <div
      ref={scrollRef}
      className="composition-piano-roll"
      data-piano-roll
      role="group"
      aria-label="Piano roll"
    >
      <svg width={totalWidth} height={totalHeight} className="composition-piano-roll-svg">
        {rows.map((midi) => (
          <rect
            key={`row-${midi}`}
            x={layout.keyboardWidth}
            y={midiToY(layout, midi)}
            width={layout.gridWidth}
            height={layout.rowHeight}
            className={isBlackKey(midi) ? 'roll-row is-black' : 'roll-row'}
          />
        ))}
        {rows.map((midi) => (
          <g key={`key-${midi}`}>
            <rect
              x={0}
              y={midiToY(layout, midi)}
              width={layout.keyboardWidth - 2}
              height={layout.rowHeight}
              className={isBlackKey(midi) ? 'roll-key is-black' : 'roll-key'}
            />
            {midi % 12 === 0 && (
              <text
                x={4}
                y={midiToY(layout, midi) + layout.rowHeight - 1}
                className="roll-key-label"
              >
                {pitchLabel(midi)}
              </text>
            )}
          </g>
        ))}
        {Array.from({ length: barCount + 1 }, (_, bar) => {
          const x = divisionsToX(layout, bar * measureDurationDivisions);
          return (
            <g key={`bar-${bar}`}>
              <line x1={x} x2={x} y1={gridTop} y2={totalHeight} className="roll-bar-line" />
              {bar < barCount && (
                <text x={x + 3} y={10} className="roll-bar-label">
                  {bar + 1}
                </text>
              )}
              {bar < barCount &&
                Array.from(
                  { length: Math.floor(measureDurationDivisions / DIVISIONS_PER_BEAT) - 1 },
                  (_, beat) => {
                    const beatX = divisionsToX(
                      layout,
                      bar * measureDurationDivisions + (beat + 1) * DIVISIONS_PER_BEAT,
                    );
                    return (
                      <line
                        key={`beat-${bar}-${beat}`}
                        x1={beatX}
                        x2={beatX}
                        y1={gridTop}
                        y2={layout.gridHeight}
                        className="roll-beat-line"
                      />
                    );
                  },
                )}
            </g>
          );
        })}
        <rect
          x={layout.keyboardWidth}
          y={gridTop}
          width={layout.gridWidth}
          height={layout.gridHeight}
          fill="transparent"
          data-roll-grid
          onPointerDown={handleGridPointerDown}
        />
        {rects.map((rect) => {
          const isSelected =
            selected.has(rect.eventId) &&
            (!selectedNoteTarget ||
              selectedNoteTarget.eventId !== rect.eventId ||
              selectedNoteTarget.midi === rect.midi);
          const dragging =
            pitchDrag &&
            pitchDrag.rect.eventId === rect.eventId &&
            pitchDrag.rect.midi === rect.midi;
          return (
            <rect
              key={`${rect.eventId}:${rect.midi}`}
              x={rect.x}
              y={dragging ? midiToY(layout, pitchDrag.midi) : rect.y}
              width={rect.width}
              height={rect.height}
              rx={1.5}
              data-roll-note={rect.eventId}
              data-roll-midi={rect.midi}
              data-roll-selected={isSelected ? 'true' : undefined}
              className={[
                'roll-note',
                rect.staff === 2 ? 'is-left' : 'is-right',
                isSelected ? 'is-selected' : '',
                reviewFlaggedEventIds?.has(rect.eventId) ? 'is-review' : '',
                dragging ? 'is-dragging' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onPointerDown={handleNotePointerDown(rect)}
              onPointerMove={handleNotePointerMove}
              onPointerUp={handleNotePointerUp}
              onPointerCancel={() => setPitchDrag(null)}
            >
              <title>
                {pitchLabel(rect.midi)}
                {rect.velocity !== null ? ` · velocity ${rect.velocity}` : ''}
              </title>
            </rect>
          );
        })}
        <line
          x1={divisionsToX(layout, recordingCursorDivisions)}
          x2={divisionsToX(layout, recordingCursorDivisions)}
          y1={gridTop}
          y2={totalHeight}
          className="roll-cursor"
          data-roll-cursor
        />
        <line
          ref={playheadRef}
          y1={gridTop}
          y2={totalHeight}
          className="roll-playhead"
          opacity={0}
        />

        <text x={4} y={velocityTop + 10} className="roll-lane-label">
          Touch
        </text>
        <rect
          x={layout.keyboardWidth}
          y={velocityTop}
          width={layout.gridWidth}
          height={layout.velocityLaneHeight}
          className="roll-lane"
          data-roll-velocity-lane
        />
        {rects.map((rect) => {
          const height = velocityBarHeight(rect.velocity, layout.velocityLaneHeight);
          return (
            <rect
              key={`v:${rect.eventId}:${rect.midi}`}
              x={rect.x}
              y={velocityTop + layout.velocityLaneHeight - height}
              width={Math.max(3, Math.min(rect.width, 8))}
              height={height}
              data-roll-velocity={rect.eventId}
              className={[
                'roll-velocity',
                rect.velocity === null ? 'is-unknown' : '',
                selected.has(rect.eventId) ? 'is-selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onPointerDown={(event) => {
                if (!editable) return;
                event.currentTarget.setPointerCapture(event.pointerId);
                applyVelocityAt(rect, event);
              }}
              onPointerMove={(event) => {
                if (!editable || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
                applyVelocityAt(rect, event);
              }}
              onPointerUp={(event) => event.currentTarget.releasePointerCapture(event.pointerId)}
            >
              <title>
                {rect.velocity === null ? 'Velocity not recorded' : `Velocity ${rect.velocity}`}
              </title>
            </rect>
          );
        })}

        <text x={4} y={pedalTop + 12} className="roll-lane-label">
          Pedal
        </text>
        <rect
          x={layout.keyboardWidth}
          y={pedalTop}
          width={layout.gridWidth}
          height={layout.pedalLaneHeight}
          className="roll-lane"
        />
        {pedalSegments.map((segment, index) => (
          <rect
            key={`pedal-${index}`}
            x={segment.x}
            y={pedalTop + 2}
            width={segment.width}
            height={layout.pedalLaneHeight - 4}
            rx={2}
            className="roll-pedal"
            data-roll-pedal
            opacity={0.35 + (segment.value / 127) * 0.65}
          />
        ))}
      </svg>
    </div>
  );
}

export const PianoRoll = memo(PianoRollComponent);
